import api from './api';
import { io, Socket } from 'socket.io-client';

/** MMS image attachment descriptor (content served by the backend proxy). */
export interface MessageAttachment {
  id: string;
  contentType: string;
  fileName?: string;
  size?: number;
}

export interface Message {
  id: string;
  type: 'SMS' | 'VOICE' | 'FAX' | 'EMAIL';
  direction: 'INBOUND' | 'OUTBOUND' | 'inbound' | 'outbound';
  from: string;
  to: string;
  text?: string;
  status: string;
  patientId?: string;
  createdAt: string;
  updatedAt: string;
  conversationId?: string;
  errorMessage?: string; // Error details for failed messages
  createdByName?: string; // Name of staff who sent the message
  attachments?: MessageAttachment[]; // MMS images (absent for plain SMS)
  /** Client-only: object URL of a just-attached image for optimistic bubbles */
  localPreviewUrl?: string;
}

export interface Template {
  id: string;
  name: string;
  text: string;
  category: string;
  variables: string[];
}

export interface BulkSMSJob {
  jobId: string;
  status: string;
  totalRecipients: number;
}

export interface CommunicationPreference {
  preferredChannel: 'SMS' | 'VOICE' | 'FAX' | 'EMAIL';
  bestTimeToContact: string;
  doNotContact: boolean;
}

// ---- Communication Center v2 (projection-backed inbox) ----

export interface ConversationListItem {
  conversationId: string;
  ringcentralConversationId: string;
  phoneNumber: string;
  patientId: string | null;
  patientName: string | null;
  status: string;
  unreadCount: number;
  isUrgent: boolean;
  departmentTag: string | null;
  aiSuggestedResponse: string | null;
  hasFailedMessages: boolean;
  failureArchived: boolean;
  /** PRX-132 — staff thread tags (values from constants/threadTags). Absent on rows from an older server. */
  threadTags?: string[];
  /** PRX-132 — ISO instant staff archived the thread; null when not archived. */
  archivedAt?: string | null;
  lastMessageAt: string;
  lastInboundAt: string | null;
  lastMessage: {
    id: string | null;
    direction: string | null; // 'inbound' | 'outbound'
    status: string | null;
    preview: string | null;
    createdByName: string | null;
  };
}

/** PRX-132 — In / Out: the direction of the thread's latest message. */
export type InboxDirection = 'in' | 'out';

export interface InboxPage {
  items: ConversationListItem[];
  nextCursor: string | null;
}

export interface InboxCounts {
  all: number;
  unread: number;
  needsReview: number;
  smsFailed: number;
  archived: number;
}

export interface ThreadPage {
  messages: Message[];
  nextBefore: string | null;
  conversation: ConversationListItem | null;
}

export type InboxTab = 'all' | 'unread' | 'needs_review' | 'sms_failed' | 'archived';

export interface InboxRangeParams {
  days?: number;
  from?: string; // YYYY-MM-DD
  to?: string; // YYYY-MM-DD
}

export interface ConversationUpsertEvent {
  pharmacyId: string;
  timestamp: string;
  conversation: ConversationListItem;
}

export interface MessageStatusUpdateEvent {
  messageId: string;
  status: string;
  phoneNumber?: string;
  errorMessage?: string;
  ringcentralMessageId?: string;
  timestamp: string;
}

export interface CommunicationAnalytics {
  totalMessages: number;
  byType: {
    SMS: number;
    VOICE: number;
    FAX: number;
  };
  byStatus: Record<string, number>;
  avgResponseTime: number;
}

class RingCentralService {
  private socket: Socket | null = null;
  private currentPharmacyId: string | null = null;

  // Callback registries to support multiple listeners per event
  private newMessageCallbacks: Set<(message: Message) => void> = new Set();
  private conversationStatusCallbacks: Set<(data: any) => void> = new Set();
  private scheduleQueueCallbacks: Set<(data: any) => void> = new Set();
  // v2 delta events (Communication Center rebuild)
  private conversationUpsertCallbacks: Set<(data: ConversationUpsertEvent) => void> = new Set();
  private messageStatusCallbacks: Set<(data: MessageStatusUpdateEvent) => void> = new Set();

  // Initialize WebSocket connection
  // Returns true if socket is connected/connecting, false if failed
  initializeSocket(token: string, pharmacyId: string): boolean {
    // Prevent duplicate socket creation - check if socket exists (even if reconnecting)
    if (this.socket) {
      // If already connected or connecting to same pharmacy, return true (already connected)
      if (this.currentPharmacyId === pharmacyId) {
        return this.socket.connected || true; // Connected or connecting
      }
      // Different pharmacy - disconnect old socket first
      this.disconnect();
    }

    this.currentPharmacyId = pharmacyId;

    const wsUrl =
      import.meta.env.VITE_WS_URL || import.meta.env.VITE_API_URL || 'http://localhost:3000';

    // Connect to /ringcentral namespace
    this.socket = io(`${wsUrl}/ringcentral`, {
      path: '/socket.io/',
      auth: {
        token,
        pharmacyId,
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    this.socket.on('connect', () => {
      // Auto-subscribe to pharmacy room
      if (this.currentPharmacyId) {
        this.socket?.emit('subscribe-pharmacy', this.currentPharmacyId);
      }
    });

    this.socket.on('disconnect', () => {
      // Connection closed
    });

    this.socket.on('error', (error) => {
      console.error('RingCentral WebSocket error:', error);
    });

    // Set up multiplexed event handlers that dispatch to all registered callbacks
    this.socket.on('new-message', (message: Message) => {
      this.newMessageCallbacks.forEach(cb => cb(message));
    });

    this.socket.on('conversation:status_update', (data: any) => {
      this.conversationStatusCallbacks.forEach(cb => cb(data));
    });

    this.socket.on('schedule-delivery-queue:update', (data: any) => {
      this.scheduleQueueCallbacks.forEach(cb => cb(data));
    });

    // v2 list-row deltas: payload is the exact ConversationListItem the inbox
    // endpoint serves, so clients patch their cache instead of refetching.
    this.socket.on('conversation-upsert', (data: ConversationUpsertEvent) => {
      this.conversationUpsertCallbacks.forEach(cb => cb(data));
    });

    this.socket.on('message-status-update', (data: MessageStatusUpdateEvent) => {
      this.messageStatusCallbacks.forEach(cb => cb(data));
    });

    return true; // Socket initialized
  }

  // ---- Communication Center v2 (projection-backed, paginated) ----

  /** Paginated conversation list. Server-side tab/tag/range/search filters. */
  async getInbox(params: {
    limit?: number;
    cursor?: string;
    tab?: InboxTab;
    departmentTag?: string;
    search?: string;
    range?: InboxRangeParams;
    /** PRX-132 — last message in/out */
    direction?: InboxDirection;
    /** PRX-132 — one thread tag */
    threadTag?: string;
  }): Promise<InboxPage> {
    const query: Record<string, any> = {
      limit: params.limit ?? 50,
      tab: params.tab ?? 'all',
    };
    if (params.cursor) query.cursor = params.cursor;
    if (params.departmentTag) query.departmentTag = params.departmentTag;
    if (params.search) query.search = params.search;
    if (params.direction) query.direction = params.direction;
    if (params.threadTag) query.threadTag = params.threadTag;
    if (params.range?.from && params.range?.to) {
      query.from = params.range.from;
      query.to = params.range.to;
    } else if (params.range?.days !== undefined) {
      query.days = params.range.days;
    }
    const response = await api.get('/api/ringcentral/v2/conversations', { params: query });
    return response.data;
  }

  /** Tab badge counts from one projection aggregate. */
  async getInboxCounts(params: {
    departmentTag?: string;
    range?: InboxRangeParams;
    direction?: InboxDirection;
    threadTag?: string;
  }): Promise<InboxCounts> {
    const query: Record<string, any> = {};
    if (params.departmentTag) query.departmentTag = params.departmentTag;
    if (params.direction) query.direction = params.direction;
    if (params.threadTag) query.threadTag = params.threadTag;
    if (params.range?.from && params.range?.to) {
      query.from = params.range.from;
      query.to = params.range.to;
    } else if (params.range?.days !== undefined) {
      query.days = params.range.days;
    }
    const response = await api.get('/api/ringcentral/v2/conversations/counts', { params: query });
    return response.data;
  }

  /** Single list-row (deep links / rows outside loaded pages). */
  async getInboxRow(phoneNumber: string): Promise<ConversationListItem | null> {
    const response = await api.get(
      `/api/ringcentral/v2/conversations/${encodeURIComponent(phoneNumber)}`,
    );
    return response.data || null;
  }

  /** One page of the message thread (newest page first; `before` pages older). */
  async getThread(phoneNumber: string, before?: string, limit = 50): Promise<ThreadPage> {
    const params: Record<string, any> = { limit };
    if (before) params.before = before;
    const response = await api.get(
      `/api/ringcentral/v2/conversations/${encodeURIComponent(phoneNumber)}/messages`,
      { params },
    );
    return response.data;
  }

  onConversationUpsert(callback: (data: ConversationUpsertEvent) => void): () => void {
    this.conversationUpsertCallbacks.add(callback);
    return () => this.conversationUpsertCallbacks.delete(callback);
  }

  /** v2 registry-based variant (legacy onMessageStatusUpdate replaces the socket listener). */
  onMessageStatus(callback: (data: MessageStatusUpdateEvent) => void): () => void {
    this.messageStatusCallbacks.add(callback);
    return () => this.messageStatusCallbacks.delete(callback);
  }

  // Send SMS
  async sendSMS(data: {
    to: string;
    text?: string;
    patientId?: string;
    orderId?: string; // Order ID for automatic SMS tracking
    templateId?: string;
    templateVariables?: Record<string, any>;
    pharmacyId: string; // Required for multi-pharmacy support
  }): Promise<Message> {
    const response = await api.post('/api/ringcentral/messages', data);
    return response.data;
  }

  // Send MMS (image message). Backend stores images in GCS and sends via the
  // RingCentral MMS API — deliberately NOT the queue-first outbox (text-only).
  async sendMMS(
    data: {
      to: string;
      text?: string;
      patientId?: string;
      orderId?: string;
    },
    files: File[],
  ): Promise<Message> {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    formData.append('to', data.to);
    if (data.text) formData.append('text', data.text);
    if (data.patientId) formData.append('patientId', data.patientId);
    if (data.orderId) formData.append('orderId', data.orderId);

    const response = await api.post('/api/ringcentral/messages/mms', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Fetch MMS attachment content as a blob (endpoint requires the auth +
  // pharmacy headers, so a plain <img src> can't load it directly)
  async getMessageAttachmentBlob(messageId: string, attachmentId: string): Promise<Blob> {
    const response = await api.get(
      `/api/ringcentral/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}`,
      { responseType: 'blob' },
    );
    return response.data;
  }

  // Retry a failed message
  async retryMessage(pharmacyId: string, messageId: string): Promise<Message> {
    const response = await api.post(`/api/ringcentral/messages/${messageId}/retry`, {}, {
      headers: { 'x-pharmacy-id': pharmacyId },
    });
    return response.data;
  }

  // Get conversations grouped by phone number.
  // Accepts either a preset number of days OR a custom { from, to } range (YYYY-MM-DD strings).
  // GP-539: custom range takes priority over `days` preset on the backend.
  // onlyArchivedFailures: when true, returns ONLY conversations with an archived failure.
  async getConversations(
    pharmacyId: string,
    range: number | { from: string; to: string } = 7,
    onlyArchivedFailures?: boolean,
  ): Promise<any> {
    // pharmacyId is automatically sent via x-pharmacy-id header by api interceptor
    const params: Record<string, any> =
      typeof range === 'number'
        ? { days: range }
        : { from: range.from, to: range.to };
    if (onlyArchivedFailures) {
      params.onlyArchivedFailures = 'true';
    }
    const response = await api.get('/api/ringcentral/conversations', { params });
    return response.data;
  }

  // Archive all unarchived outbound failed comms in a conversation (reversible)
  async archiveConversationFailure(phoneNumber: string, pharmacyId: string): Promise<void> {
    try {
      const encodedPhone = encodeURIComponent(phoneNumber);
      await api.put(`/api/ringcentral/conversations/${encodedPhone}/archive-failure`, {}, {
        headers: { 'x-pharmacy-id': pharmacyId },
      });
    } catch (error: any) {
      console.error(`[RingCentral] Failed to archive conversation failure:`, {
        phoneNumber,
        pharmacyId,
        error: error.message,
        response: error.response?.data,
      });
      throw error;
    }
  }

  // Unarchive archived failed comms in a conversation (reverse of archive)
  async unarchiveConversationFailure(phoneNumber: string, pharmacyId: string): Promise<void> {
    try {
      const encodedPhone = encodeURIComponent(phoneNumber);
      await api.put(`/api/ringcentral/conversations/${encodedPhone}/unarchive-failure`, {}, {
        headers: { 'x-pharmacy-id': pharmacyId },
      });
    } catch (error: any) {
      console.error(`[RingCentral] Failed to unarchive conversation failure:`, {
        phoneNumber,
        pharmacyId,
        error: error.message,
        response: error.response?.data,
      });
      throw error;
    }
  }

  // Get lightweight conversation counts for tab badges (more efficient than getConversations)
  // GP-537: counts are scoped by days + departmentTag so badges match visible rows.
  async getConversationCounts(
    pharmacyId: string,
    options?: { days?: number; departmentTag?: string }
  ): Promise<{ all: number; unread: number; needsReview: number; smsFailed: number }> {
    // pharmacyId is automatically sent via x-pharmacy-id header by api interceptor
    const params: Record<string, any> = {};
    if (options?.days !== undefined) params.days = options.days;
    if (options?.departmentTag) params.departmentTag = options.departmentTag;
    const response = await api.get('/api/ringcentral/conversation-counts', { params });
    return response.data;
  }

  // Get single conversation by phone number (fast path for dialogs)
  // Uses optimized SQL query - much faster than loading all conversations
  // days: optional time range filter (30, 90, 365, or 0 for all)
  async getConversationByPhone(pharmacyId: string, phoneNumber: string, days?: number): Promise<any> {
    const params: Record<string, any> = {};
    if (days !== undefined) {
      params.days = days;
    }
    const response = await api.get(
      `/api/ringcentral/conversations/phone/${encodeURIComponent(phoneNumber)}`,
      { headers: { 'x-pharmacy-id': pharmacyId }, params }
    );
    return response.data;
  }

  // Get a specific conversation by RingCentral conversation ID
  // SYSTEMATIC IDENTIFICATION: Use this to resolve conversation ID to phone number
  async getConversationByRingCentralId(conversationId: string, pharmacyId: string): Promise<any> {
    try {
      // FIXED: Correct path is /api/conversations/ringcentral/:id (not /api/ringcentral/conversations)
      const response = await api.get(`/api/conversations/ringcentral/${conversationId}`, {
        headers: { 'x-pharmacy-id': pharmacyId },
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch conversation by RingCentral ID:', error);
      throw error;
    }
  }

  // Send bulk SMS
  async sendBulkSMS(data: {
    recipients: Array<{
      phoneNumber: string;
      patientId?: string;
      variables?: Record<string, any>;
    }>;
    message: string;
    templateId?: string;
    scheduledAt?: string;
    pharmacyId: string; // Required for multi-pharmacy support
  }): Promise<BulkSMSJob> {
    const response = await api.post('/api/ringcentral/messages/bulk', data);
    return response.data;
  }

  // Template management
  async getTemplates(pharmacyId: string): Promise<Template[]> {
    // pharmacyId is automatically sent via x-pharmacy-id header by api interceptor
    const response = await api.get('/api/ringcentral/templates');
    return response.data;
  }

  async createTemplate(
    pharmacyId: string,
    data: {
      name: string;
      text: string;
      category: string;
    },
  ): Promise<Template> {
    // pharmacyId is automatically sent via x-pharmacy-id header by api interceptor
    const response = await api.post('/api/ringcentral/templates', data);
    return response.data;
  }

  async updateTemplate(
    id: string,
    pharmacyId: string,
    data: {
      name?: string;
      text?: string;
      category?: string;
    },
  ): Promise<Template> {
    // pharmacyId is automatically sent via x-pharmacy-id header by api interceptor
    const response = await api.put(`/ringcentral/templates/${id}`, data);
    return response.data;
  }

  async deleteTemplate(id: string, pharmacyId: string): Promise<void> {
    // pharmacyId is automatically sent via x-pharmacy-id header by api interceptor
    await api.delete(`/api/ringcentral/templates/${id}`);
  }

  // Get message history (backwards compatibility - returns empty for now)
  async getMessageHistory(params: {
    patientId?: string;
    phoneNumber?: string;
    type?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }): Promise<any[]> {
    // For backwards compatibility, return empty array
    // Note: Use getConversations() instead for new implementations
    return [];
  }

  // Make voice call (stub for backwards compatibility)
  async makeCall(data: {
    to: string;
    patientId?: string;
    notes?: string;
    pharmacyId: string;
  }): Promise<Message> {
    throw new Error('Voice calls not yet supported');
  }

  // Sync messages from RingCentral
  async syncMessages(
    pharmacyId: string,
  ): Promise<{ synced: number; newMessages: number; updatedMessages: number; errors: number }> {
    // pharmacyId is automatically sent via x-pharmacy-id header by api interceptor
    const response = await api.post('/api/ringcentral/messages/sync', {});
    return response.data;
  }

  // Sync a specific conversation from RingCentral
  // days: optional time range for how far back to sync (30, 90, 365)
  async syncConversation(
    pharmacyId: string,
    phoneNumber: string,
    conversationId?: string,
    days?: number,
  ): Promise<{ synced: number; newMessages: number; updatedMessages: number }> {
    // pharmacyId is automatically sent via x-pharmacy-id header by api interceptor
    const response = await api.post(
      `/api/ringcentral/conversations/${encodeURIComponent(phoneNumber)}/sync`,
      { conversationId, days },
    );
    return response.data;
  }

  // Mark conversation as read
  async markConversationRead(phoneNumber: string, pharmacyId: string): Promise<void> {
    try {
      // Encode phone number to handle special characters like +
      const encodedPhone = encodeURIComponent(phoneNumber);

      // pharmacyId is automatically sent via x-pharmacy-id header by api interceptor
      await api.put(`/api/ringcentral/conversations/${encodedPhone}/read`, {});
    } catch (error: any) {
      console.error(`[RingCentral] Failed to mark conversation as read:`, {
        phoneNumber,
        pharmacyId,
        error: error.message,
        response: error.response?.data
      });
      throw error;
    }
  }

  // Mark conversation as unread (manual staff action — GP-532)
  async markConversationUnread(phoneNumber: string, pharmacyId: string): Promise<void> {
    try {
      const encodedPhone = encodeURIComponent(phoneNumber);
      // pharmacyId is automatically sent via x-pharmacy-id header by api interceptor
      await api.put(`/api/ringcentral/conversations/${encodedPhone}/unread`, {});
    } catch (error: any) {
      console.error(`[RingCentral] Failed to mark conversation as unread:`, {
        phoneNumber,
        pharmacyId,
        error: error.message,
        response: error.response?.data,
      });
      throw error;
    }
  }

  // Clear a handled conversation out of the Needs Review bucket (GP-1435).
  // Leaves the thread in All with its history — this is not an archive.
  async markConversationReviewed(phoneNumber: string, pharmacyId: string): Promise<void> {
    try {
      const encodedPhone = encodeURIComponent(phoneNumber);
      // pharmacyId is automatically sent via x-pharmacy-id header by api interceptor
      await api.put(`/api/ringcentral/conversations/${encodedPhone}/reviewed`, {});
    } catch (error: any) {
      console.error(`[RingCentral] Failed to mark conversation as reviewed:`, {
        phoneNumber,
        pharmacyId,
        error: error.message,
        response: error.response?.data,
      });
      throw error;
    }
  }

  // Regenerate AI suggestion for a conversation
  async regenerateAiSuggestion(
    conversationId: string,
    pharmacyId: string
  ): Promise<{
    success: boolean;
    aiSuggestedResponse: string;
    isUrgent: boolean;
    intent: string;
    confidence: number;
  }> {
    const response = await api.post(
      `/api/ringcentral/conversations/${conversationId}/regenerate-suggestion`,
      {},
      { headers: { 'x-pharmacy-id': pharmacyId } }
    );
    return response.data;
  }

  // Test WebSocket connection
  async testWebSocket(): Promise<any> {
    // pharmacyId is automatically sent via x-pharmacy-id header by api interceptor
    const response = await api.post('/api/ringcentral/test-websocket', {});
    return response.data;
  }

  // T051: Get accurate Needs Review count from backend
  async getNeedsReviewCount(pharmacyId: string): Promise<number> {
    const response = await api.get(`/api/conversations/needs-review-count/${pharmacyId}`);
    return response.data?.data?.count || 0;
  }

  // T053 / GP-533: Tag conversation with department (billing | clinical | logistics)
  async tagConversation(
    conversationId: string,
    departmentTag: string,
    userId: string,
  ): Promise<any> {
    const response = await api.post(`/api/conversations/${conversationId}/tag`, {
      departmentTag,
      userId,
    });
    return response.data;
  }

  // GP-533: Remove the department tag from a conversation
  async untagConversation(conversationId: string): Promise<any> {
    const response = await api.delete(`/api/conversations/${conversationId}/tag`);
    return response.data;
  }

  // Get conversations filtered by department tag
  async getConversationsByTag(pharmacyId: string, departmentTag: string): Promise<any[]> {
    const response = await api.get(`/api/conversations/by-tag/${pharmacyId}/${departmentTag}`);
    return response.data?.data || [];
  }

  // WebSocket methods
  subscribeToConversation(phoneNumber: string): void {
    if (this.currentPharmacyId) {
      this.socket?.emit('subscribe-conversation', {
        phoneNumber,
        pharmacyId: this.currentPharmacyId,
      });
    }
  }

  unsubscribeFromConversation(phoneNumber: string): void {
    if (this.currentPharmacyId) {
      this.socket?.emit('unsubscribe-conversation', {
        phoneNumber,
        pharmacyId: this.currentPharmacyId,
      });
    }
  }

  onNewMessage(callback: (message: Message) => void): () => void {
    // Add to callback registry (supports multiple listeners)
    this.newMessageCallbacks.add(callback);
    // Return unsubscribe function
    return () => this.newMessageCallbacks.delete(callback);
  }

  onConversationUpdated(callback: (data: any) => void): void {
    // Remove previous listeners for this event to prevent duplicates
    this.socket?.off('conversation-updated');
    this.socket?.on('conversation-updated', callback);
  }

  onMessageStatusUpdate(callback: (data: any) => void): void {
    // Remove previous listeners for this event to prevent duplicates
    this.socket?.off('message-status-update');
    this.socket?.on('message-status-update', callback);
  }

  onSyncComplete(callback: (data: any) => void): void {
    // Remove previous listeners for this event to prevent duplicates
    this.socket?.off('sync-complete');
    this.socket?.on('sync-complete', callback);
  }

  onConversationStatusUpdate(
    callback: (data: {
      conversationId: string;
      phoneNumber: string; // SYSTEMATIC TRACKING: Primary identifier for conversations
      pharmacyId: string;
      status: string;
      unreadCount: number;
      isUrgent: boolean;
      aiSuggestedResponse?: string;
      testMode?: boolean;
      testMessage?: string;
    }) => void,
  ): () => void {
    // Add to callback registry (supports multiple listeners)
    this.conversationStatusCallbacks.add(callback);
    // Return unsubscribe function
    return () => this.conversationStatusCallbacks.delete(callback);
  }

  onScheduleDeliveryQueueUpdate(callback: (data: any) => void): () => void {
    // Add to callback registry (supports multiple listeners)
    this.scheduleQueueCallbacks.add(callback);
    // Return unsubscribe function
    return () => this.scheduleQueueCallbacks.delete(callback);
  }

  onCallCompleted(callback: (call: Message) => void): void {
    // Remove previous listeners for this event to prevent duplicates
    this.socket?.off('call:completed');
    this.socket?.on('call:completed', callback);
  }

  onFaxSent(callback: (fax: Message) => void): void {
    // Remove previous listeners for this event to prevent duplicates
    this.socket?.off('fax:sent');
    this.socket?.on('fax:sent', callback);
  }

  // Clear all callback registries (but keep socket-level multiplexed handlers)
  removeAllListeners(): void {
    this.newMessageCallbacks.clear();
    this.conversationStatusCallbacks.clear();
    this.scheduleQueueCallbacks.clear();
    this.conversationUpsertCallbacks.clear();
    this.messageStatusCallbacks.clear();
  }

  // Disconnect socket (only call on app unmount, not component unmount)
  disconnect(): void {
    // Clear all callback registries
    this.newMessageCallbacks.clear();
    this.conversationStatusCallbacks.clear();
    this.scheduleQueueCallbacks.clear();
    this.conversationUpsertCallbacks.clear();
    this.messageStatusCallbacks.clear();
    // Disconnect and cleanup socket
    this.socket?.disconnect();
    this.socket = null;
    this.currentPharmacyId = null;
  }

  // Check connection status
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  // Format phone number for display
  formatPhoneNumber(phoneNumber: string): string {
    if (!phoneNumber) return '';
    const digits = phoneNumber.replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length === 11 && digits[0] === '1') {
      return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    return phoneNumber;
  }

  // Apply template with variable substitution
  applyTemplate(template: string, variables: Record<string, any>): string {
    let result = template;
    Object.entries(variables).forEach(([key, value]) => {
      const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
      result = result.replace(regex, String(value || ''));
    });
    return result;
  }
}

export const ringCentralService = new RingCentralService();
